import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { copyPageVariables } from '@/services/api/broadcast';
import { getPageVariables } from '@/services/api/connections';
import { LANGUAGES, TIMEZONES } from '../constants/config-options';
import type { MetaPage, PageVariable } from '../types/meta.types';

interface PageVariablesCopyModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentPage: MetaPage;
    pages: MetaPage[];
    onSuccess: () => void;
}

export default function PageVariablesCopyModal({
    open,
    onOpenChange,
    currentPage,
    pages,
    onSuccess,
}: PageVariablesCopyModalProps) {
    const [sourcePageId, setSourcePageId] = useState<string>(() => String(currentPage.id));
    const [targetIds, setTargetIds] = useState<Set<number>>(() => new Set());
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Destination filters
    const [filterCategory, setFilterCategory] = useState<string>('');
    const [filterLanguage, setFilterLanguage] = useState<string>('');
    const [filterTimezone, setFilterTimezone] = useState<string>('');

    const { data: sourceVariables = [], isLoading: loadingVariables } = useQuery({
        queryKey: ['page-variables', sourcePageId],
        queryFn: () => getPageVariables(Number(sourcePageId)),
        enabled: open && !!sourcePageId,
    });

    const [overrides, setOverrides] = useState<Record<string, string>>({});

    useEffect(() => {
        const initial: Record<string, string> = {};
        for (const v of sourceVariables) {
            initial[v.key] = v.value;
        }
        setOverrides(initial);
    }, [sourcePageId, sourceVariables]);

    const effectiveOverrides = (sourceVariables as PageVariable[]).reduce<Record<string, string>>((acc, v) => {
        acc[v.key] = overrides[v.key] ?? v.value;
        return acc;
    }, {});

    const handleOverrideChange = (key: string, value: string) => {
        setOverrides(prev => ({ ...prev, [key]: value }));
    };

    // Source options — all pages
    const pageOptions = pages.map(p => ({
        value: String(p.id),
        label: p.page_name,
    }));

    // Category options derived from pages (unique non-null values)
    const categoryOptions = useMemo(() => {
        const seen = new Set<string>();
        const opts: { value: string; label: string }[] = [{ value: '', label: 'Todas as categorias' }];
        for (const p of pages) {
            if (p.internal_category_name && !seen.has(p.internal_category_name)) {
                seen.add(p.internal_category_name);
                opts.push({ value: p.internal_category_name, label: p.internal_category_name });
            }
        }
        return opts;
    }, [pages]);

    const languageOptions = useMemo(() => [
        { value: '', label: 'Todos os idiomas' },
        ...LANGUAGES,
    ], []);

    const timezoneOptions = useMemo(() => [
        { value: '', label: 'Todos os fusos' },
        ...TIMEZONES,
    ], []);

    // Candidate pages: exclude current source
    const candidatePages = useMemo(() => {
        return pages.filter(p => String(p.id) !== sourcePageId);
    }, [pages, sourcePageId]);

    // Filtered pages based on active filters
    const filteredPages = useMemo(() => {
        return candidatePages.filter(p => {
            if (filterCategory && p.internal_category_name !== filterCategory) return false;
            if (filterLanguage && p.language !== filterLanguage) return false;
            if (filterTimezone && p.timezone !== filterTimezone) return false;
            return true;
        });
    }, [candidatePages, filterCategory, filterLanguage, filterTimezone]);

    // When filters change, keep only selections still visible in filtered list
    useEffect(() => {
        const visibleIds = new Set(filteredPages.map(p => p.id));
        setTargetIds(prev => {
            const next = new Set<number>();
            for (const id of prev) {
                if (visibleIds.has(id)) next.add(id);
            }
            return next;
        });
    }, [filteredPages]);

    // When source changes, clear destination selection
    useEffect(() => {
        setTargetIds(new Set());
    }, [sourcePageId]);

    const allFilteredSelected =
        filteredPages.length > 0 && filteredPages.every(p => targetIds.has(p.id));

    const handleSelectAll = () => {
        if (allFilteredSelected) {
            setTargetIds(prev => {
                const next = new Set(prev);
                for (const p of filteredPages) next.delete(p.id);
                return next;
            });
        } else {
            setTargetIds(prev => {
                const next = new Set(prev);
                for (const p of filteredPages) next.add(p.id);
                return next;
            });
        }
    };

    const handleToggle = (id: number) => {
        setTargetIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleCopy = async () => {
        if (!sourcePageId || targetIds.size === 0) return;
        setLoading(true);
        setError(null);
        try {
            await copyPageVariables({
                source_page_id: Number(sourcePageId),
                target_page_ids: Array.from(targetIds),
                overrides_per_page: Array.from(targetIds).map(() => ({ ...effectiveOverrides })),
            });
            setDone(true);
            setTimeout(() => {
                onSuccess();
                onOpenChange(false);
            }, 800);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Erro ao copiar variáveis');
        } finally {
            setLoading(false);
        }
    };

    const canCopy = sourcePageId && targetIds.size > 0 && sourceVariables.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="h-5 w-5 text-blue-600" />
                        Copiar variáveis entre páginas
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    {/* 1. Origem */}
                    <div className="space-y-2">
                        <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Copiar de (origem)
                        </Label>
                        <SearchableSelect
                            value={sourcePageId}
                            onValueChange={setSourcePageId}
                            options={pageOptions}
                            placeholder="Selecione a página de origem"
                            searchPlaceholder="Buscar origem..."
                        />
                    </div>

                    {/* 2. Destino — filtros + lista com checkboxes */}
                    <div className="space-y-3">
                        <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Destino
                        </Label>

                        {/* Filtros side-by-side */}
                        <div className="grid grid-cols-3 gap-3">
                            <SearchableSelect
                                value={filterCategory}
                                onValueChange={setFilterCategory}
                                options={categoryOptions}
                                placeholder="Categoria"
                                searchPlaceholder="Buscar categoria..."
                            />
                            <SearchableSelect
                                value={filterLanguage}
                                onValueChange={setFilterLanguage}
                                options={languageOptions}
                                placeholder="Idioma"
                                searchPlaceholder="Buscar idioma..."
                            />
                            <SearchableSelect
                                value={filterTimezone}
                                onValueChange={setFilterTimezone}
                                options={timezoneOptions}
                                placeholder="Fuso horário"
                                searchPlaceholder="Buscar fuso..."
                            />
                        </div>

                        {/* Selecionar todas */}
                        {filteredPages.length > 0 && (
                            <div className="flex items-center gap-2 px-1">
                                <Checkbox
                                    id="select-all"
                                    checked={allFilteredSelected}
                                    onCheckedChange={handleSelectAll}
                                />
                                <label
                                    htmlFor="select-all"
                                    className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                                >
                                    Selecionar todas ({filteredPages.length} página{filteredPages.length !== 1 ? 's' : ''})
                                </label>
                            </div>
                        )}

                        {/* Lista de páginas */}
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/50">
                            {filteredPages.length === 0 ? (
                                <p className="px-4 py-3 text-sm text-gray-500 text-center">
                                    Nenhuma página encontrada com os filtros aplicados.
                                </p>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {filteredPages.map(p => (
                                        <li key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white transition-colors">
                                            <Checkbox
                                                id={`page-${p.id}`}
                                                checked={targetIds.has(p.id)}
                                                onCheckedChange={() => handleToggle(p.id)}
                                            />
                                            <label
                                                htmlFor={`page-${p.id}`}
                                                className="flex flex-1 items-center gap-2 cursor-pointer select-none min-w-0"
                                            >
                                                <span className="truncate text-sm font-medium text-gray-800">
                                                    {p.page_name}
                                                </span>
                                                {p.language && (
                                                    <span className="shrink-0 text-xs text-gray-400">{p.language}</span>
                                                )}
                                                {p.timezone && (
                                                    <span className="shrink-0 text-xs text-gray-400">{p.timezone}</span>
                                                )}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Contador */}
                        <p className="text-xs text-gray-500 px-1">
                            {targetIds.size} página{targetIds.size !== 1 ? 's' : ''} selecionada{targetIds.size !== 1 ? 's' : ''}
                        </p>
                    </div>

                    {/* 3. Variáveis editáveis */}
                    {loadingVariables ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : sourceVariables.length === 0 ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 text-sm text-amber-800">
                            A página de origem não possui variáveis cadastradas.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">
                                Valores a copiar (aplicados igualmente a todos os destinos)
                            </p>
                            <div className="space-y-3 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                                {(sourceVariables as PageVariable[]).map((v) => (
                                    <div key={v.key} className="space-y-1">
                                        <Label className="text-xs font-mono text-gray-600">{`{{${v.key}}}`}</Label>
                                        <Input
                                            value={effectiveOverrides[v.key] ?? v.value}
                                            onChange={e => handleOverrideChange(v.key, e.target.value)}
                                            placeholder={`Valor para ${v.key}`}
                                            className="text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleCopy}
                        disabled={loading || done || !canCopy}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {done
                            ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Copiado!</>
                            : loading
                                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Copiando...</>
                                : <><Copy className="h-4 w-4 mr-1" /> Copiar variáveis</>
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
